import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Box, TextInput, Textarea, Select, ActionIcon, createStyles, Text, Button } from "@mantine/core";
import { GripVertical, X } from "tabler-icons-react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Answers from "./Answers";

import QuestionModel from "../../../../models/Question";
import AnswerModel from "../../../../models/Answer";

import { colors } from "../../../../collections/colors";
import { setTiptapSidemenu } from "../../../../store/app";
import { useDebounce } from "../../../hooks/useDebounce";

const useStyles = createStyles((theme) => ({
  item: {
    display: "flex",
    alignItems: "flex-start",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[4]}`,
    paddingTop: 15,
    paddingBottom: 15,
    paddingRight: 15,
    backgroundColor: theme.white,
    marginTop: 15,
    width: "100%",
  },
  questionWrap: {
    width: "100%",
  },
  dragHandle: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    height: "100%",
    cursor: "move",
    color: theme.colors.gray[6],
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 8,
  },
}));

const questionOptions = [
  { value: "multiple", label: "Multiple choice" },
  { value: "single", label: "Single choice" },
  { value: "freeform", label: "Short Answer" },
  { value: "member_property", label: "Member property" },
  { value: "payment", label: "Payment" },
];

const SurveyQuestion = (props) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const [questionType, setQuestionType] = useState("multiple");
  const [answerKey, setAnswerKey] = useState("");
  const [content, setContent] = useState("");
  const [questionId, setQuestionId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [filterId, setFilterId] = useState("");
  const [loaded, setLoaded] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.question?.id || "new" });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const loading = useRef(false);

  const filters = Array.isArray(props.filters) ? props.filters : [];
  const usedFilterIds = Array.isArray(props.usedFilterIds) ? props.usedFilterIds.filter((id) => id !== Number(filterId)) : [];
  const availableFilters = filters.filter((filter) => !usedFilterIds.includes(filter.id));
  const filtersOptions = availableFilters.map((filter) => ({ value: String(filter.id), label: filter.name }));

  useEffect(() => {
    return () => {
      loading.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !loading.current &&
      props.question &&
      !props.question.id &&
      props.question.new
    ) {
      onCreate();
    } else if (props.question && !loaded) {
      setQuestionId(null);
      onFetchById();
      setLoaded(true);
    }
  }, [props, loaded]);

  useEffect(() => {
    if (loading.current) {
      onUpdate(content, questionType);
    }
  }, [answerKey]);

  const onFetchById = () => {
    QuestionModel.onFetchById(
      props.question.id,
      props.surveyId,
      props.spaceId,
      (data) => {
        setQuestionId(props.question.id);
        if (data.question_type) {
          setQuestionType(
            !props.acceptPayments ? data.question_type : "payment"
          );
        } else {
          setQuestionType(!props.acceptPayments ? "multiple" : "payment");
        }
        if (data.pod_member_filter_id) {
          setFilterId(String(data.pod_member_filter_id));
        }
        if (data.content) {
          setContent(data.content);
        } else {
          if (props.acceptPayments) {
            setContent("Payment question");
          }
        }
        if (data.answers) {
          setAnswers(data.answers);
        }
        setAnswerKey(data.answer_key);
        setTimeout(() => {
          loading.current = true;
        }, 1000);
      },
      (data) => {}
    );
  };

  const getQuestionOptions = () => {
    if (props.acceptPayments) {
      return questionOptions;
    } else {
      const aux = [...questionOptions];
      aux.pop();
      return aux;
    }
  };

  const onQuestionTypeChange = (value) => {
    if (value == "freeform") {
      onDestroyAll();
    } else {
      setQuestionType(value);
    }
    onUpdate(content, value);
  };

  const onFilterTypeChange = (id) => {
    setFilterId(id);
    const filter = filters.find((filter) => filter.id === Number(id));
    onUpdate(content, questionType, filter.id);
  };

  const onContentChange = (value) => {
    setContent(value);
    onUpdate(value, questionType);
  };

  const onCreate = () => {
    setDisabled(true)
    QuestionModel.onCreate(
      props.spaceId,
      props.surveyId,
      { content: props.question.content },
      (data) => {
        setQuestionId(data.id);
        props.onUpdateLast(data.id);
        setTimeout(() => {
          loading.current = true;
          onPushNewAnswer()
          setDisabled(false)
        }, 1000);
      },
      (data) => {}
    );
  };

  const onUpdate = (content, type, pod_member_filter_id) => {
    if (!questionId) {
      return;
    }
    debouncedUpdate({ content, type, pod_member_filter_id });
  };

  const debouncedUpdate = useDebounce(({ content, type, pod_member_filter_id }) => {
    QuestionModel.onUpdate(
      questionId,
      props.spaceId,
      props.surveyId,
      { question_type: type, content, answer_key: answerKey, pod_member_filter_id },
      (data) => {
        if (typeof props.onUpdateQuestions === "function") {
          props.onUpdateQuestions(); 
        }
      },
      (data) => {}
    );
  }, 1000);

  const onDestroy = () => {
    if (!questionId) {
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      html: "All collected question responses will be deleted.",
      showDenyButton: true,
      confirmButtonText: "Yes, delete",
      denyButtonText: "No",
      confirmButtonColor: colors.red,
      denyButtonColor: colors.gray,
    }).then((result) => {
      if (result.isConfirmed) {
        QuestionModel.onDestroy(
          questionId,
          props.spaceId,
          props.surveyId,
          (data) => {
            props.onDestroy({ id: questionId });
          },
          (data) => {}
        );
      } else if (result.isDenied) {
      }
    });
  };

  const onDestroyAll = () => {
    if (!questionId) {
      return;
    }

    AnswerModel.onDestroyAll(
      props.spaceId,
      props.surveyId,
      questionId,
      (data) => {
        setQuestionType("freeform");
      },
      (data) => {}
    );
  };

  const onPushNewAnswer = () => {
    const newAnswers = [...answers];
    if (!window.$tmpAnswersId){
      window.$tmpAnswersId = 0
    }

    newAnswers.push({ new: true, content: `Answer ${answers.length + 1}`, tmp_id: window.$tmpAnswersId, frequency: "once" });
    setAnswers(newAnswers);
  };

  const onAnswersUpdate = (_answers, _questionType = null, _content = null) => {
    const newAnswers = [..._answers];

    setAnswers(newAnswers);
    const newQuestion = { ...props.question };
    newQuestion.answers = newAnswers;
    newQuestion.content = _content ? _content : content;
    newQuestion.question_type = _questionType ? _questionType : questionType;

    props.onUpdate(newQuestion, props.index);
  };

  return (
    <Box
      className={classes.item}
      ref={setNodeRef}
      style={style}
      spacing={0}
      p="xs"
      bg="gray.0"
    >
      {!props.acceptPayments && (
        <div className={classes.dragHandle} {...listeners} {...attributes}>
          <GripVertical size={18} />
        </div>
      )}
      <div className={classes.questionWrap}>
        {!props.acceptPayments && (
          <TextInput
            value={content}
            onChange={(event) => onContentChange(event.currentTarget.value)}
            placeholder={`Question`}
            radius="sm"
            size="md"
            rightSection={
              <ActionIcon variant="transparent" onClick={() => onDestroy()}>
                <X size={18} />
              </ActionIcon>
            }
            rightSectionWidth={40}
            disabled={disabled}
          />
        )}
        {!props.acceptPayments && (
          <Select
            mt={10}
            value={questionType}
            onChange={onQuestionTypeChange}
            data={getQuestionOptions()}
            radius="md"
            size="sm"
            fullWidth
          />
        )}

        {!props.acceptPayments && questionType === "member_property" && (
          <>
            {filtersOptions.length > 0 ? (
              <Select
                mt={10}
                value={filterId}
                onChange={onFilterTypeChange}
                data={filtersOptions}
                placeholder="Please select a member property"
                radius="md"
                size="sm"
                fullWidth
              />
            ) : (
              <Text size="sm" color="dimmed" pt={20} pb={10}>
                No member properties have been created
              </Text>
            )}
            <Button
              variant="default"
              onClick={() => {
                dispatch(setTiptapSidemenu({ opened: "filter-members", option: window.$fetchedTopSlug }));
              }}
              leftIcon={<FontAwesomeIcon icon={solid("cog")} color="grey" />}
              radius="md"
              size="xs"
              mt={10}
            >
              <Text color="grey" size="xs">Manage properties</Text>
            </Button>
          </>
        )}

        {props.quizMode && !["freeform", "member_property"].includes(questionType) && (
          <Text mt={10} size="sm" color="dimmed">{`Select the correct answer below`}</Text>
        )}

        {questionType !== "member_property" && (
          <Answers
            type={questionType}
            answers={answers}
            surveyId={props.surveyId}
            spaceId={props.spaceId}
            questionId={questionId}
            onUpdate={onAnswersUpdate}
            currency={props.currency}
            quizMode={props.quizMode}
          />
        )}

        {props.showKeys && (
          <Textarea
            label="Answer key (optional)"
            description="The answer key appears after the participant has submitted the entire quiz"
            value={answerKey}
            onChange={(event) => setAnswerKey(event.currentTarget.value)}
            radius="sm"
            size="md"
            autosize={true}
          />
        )}
      </div>
    </Box>
  );
};

export default SurveyQuestion;
